import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { WizardChoiceChangedModal } from '../components/WizardChoiceChangedModal';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation, nextIncompleteRoute } from '../lib/wizardSteps';
import { saveAndNavigate } from '../lib/navigationHelpers';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { useCreditPricing } from '../hooks/useCreditPricing';
import { PenLine, Sparkles } from 'lucide-react';

export function SelectTitle() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, refreshSession } = useSession();
  const sessionId = sessionIdFromLocation(location);
  const { pricing } = useCreditPricing();
  const introCost = pricing['intros_generate'];

  const [showCustom, setShowCustom] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  // Titel die wacht op het antwoord in de modal (intro's opnieuw of houden).
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sid: string) => {
    try {
      setLoading(true);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sid)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('Error loading session:', sessionError);
        setLoading(false);
        return;
      }

      setDashboardSession(sessionData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const titles: string[] = Array.isArray(activeSession?.titles) ? activeSession.titles : [];
  const steps = getWizardSteps(activeSession, 4);

  const goTo = (path: string) => navigate(wizardUrl(path, activeSession?.id));
  const goBack = () => {
    const back = backUrlFor(WIZARD_PATHS.selectTitle, activeSession?.id);
    if (back) navigate(back);
  };
  const header = (
    <FocusHeader
      title="Titel kiezen"
      onBack={goBack}
      onClose={() => navigate(WIZARD_EXIT_ROUTE)}
      hideSaveStatus
    />
  );

  // Sla de titel op en ga door. redoIntros = intro's wissen zodat ze opnieuw
  // geschreven worden bij de nieuwe titel. Afbeeldingen blijven altijd staan.
  const commitTitle = async (title: string, redoIntros: boolean) => {
    if (saving || !activeSession?.id) return;

    try {
      setSaving(true);
      const updates: Record<string, unknown> = { selected_title: title };
      if (redoIntros) {
        updates.intros = null;
        updates.selected_intro = null;
      }
      const next = { ...activeSession, ...updates };
      const target = redoIntros ? WIZARD_PATHS.introGenerate : nextIncompleteRoute(next);

      await saveAndNavigate(
        activeSession.id,
        updates,
        ['selected_title'],
        navigate,
        wizardUrl(target, activeSession.id),
      );

      if (refreshSession) {
        await refreshSession();
      }
    } catch (error) {
      console.error('Error saving title:', error);
      alert('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
      setSaving(false);
    }
  };

  const chooseTitle = (title: string) => {
    const clean = title.trim();
    if (!clean || saving || !activeSession) return;

    if (clean === activeSession.selected_title) {
      // Zelfde titel: niets te bewaren, gewoon door.
      goTo(nextIncompleteRoute(activeSession));
      return;
    }

    const hasIntros = Array.isArray(activeSession.intros) && activeSession.intros.length > 0;
    if (hasIntros || activeSession.selected_intro) {
      // Er zijn al intro's (of een gekozen intro) bij de vorige titel: opnieuw schrijven kost een credit, dus vragen.
      setPendingTitle(clean);
      return;
    }

    // Nog geen intro's: die horen bij de titel, dus door naar het schrijven ervan.
    void commitTitle(clean, true);
  };

  const handleCustomTitle = () => {
    if (!customTitle.trim()) {
      alert('Voer eerst een titel in.');
      return;
    }
    chooseTitle(customTitle);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">Geen sessie gevonden. Open de blog opnieuw vanuit je concepten.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary mt-4">
              Naar Mijn Blogs
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (titles.length === 0) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <StepIndicator steps={steps} onStepClick={handleStepClick} />
          <div className="card mt-6 text-center space-y-4">
            <p className="text-gray-700">Er zijn nog geen titels voor dit artikel.</p>
            <button
              onClick={() => goTo(WIZARD_PATHS.titleGenerate)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Titels laten schrijven
            </button>
          </div>
        </main>
      </div>
    );
  }

  const introChosen = !!activeSession.selected_intro;

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      {header}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              Kies je favoriete titel
            </h1>
            <p className="text-gray-600">Selecteer een titel of schrijf je eigen</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {titles.map((title: string, index: number) => {
              const isChosen = title === activeSession.selected_title;
              return (
                <button
                  key={index}
                  onClick={() => chooseTitle(title)}
                  disabled={saving}
                  aria-pressed={isChosen}
                  className={`card text-left hover:border-[#231F20] hover:shadow-lg transition-all p-6 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isChosen ? 'border-[#231F20] ring-2 ring-[#231F20]/15' : ''
                  }`}
                >
                  <p className="font-semibold text-[#231F20] leading-tight">
                    {title}
                  </p>
                  {isChosen && (
                    <p className="text-xs text-green-600 font-medium mt-2">Gekozen</p>
                  )}
                </button>
              );
            })}

            {!showCustom ? (
              <button
                onClick={() => setShowCustom(true)}
                className="card text-center hover:border-[#231F20] hover:shadow-lg transition-all p-6 border-dashed"
              >
                <PenLine className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="font-semibold text-gray-600">
                  Schrijf eigen titel
                </p>
              </button>
            ) : (
              <div className="card p-6 border-[#231F20] bg-blue-50">
                <textarea
                  className="textarea-field min-h-[80px] mb-3"
                  placeholder="Schrijf je eigen titel..."
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCustomTitle}
                    disabled={!customTitle.trim() || saving}
                    className="btn-primary flex-1 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Gebruik deze titel
                  </button>
                  <button
                    onClick={() => {
                      setShowCustom(false);
                      setCustomTitle('');
                    }}
                    className="btn-secondary py-2 text-sm"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <WizardChoiceChangedModal
        isOpen={pendingTitle !== null}
        title="Titel gewijzigd"
        description={
          introChosen
            ? "De intro is geschreven bij de vorige titel. Wil je nieuwe intro's laten schrijven voor deze titel, of de huidige intro houden?"
            : "De intro's zijn geschreven bij de vorige titel. Wil je nieuwe intro's laten schrijven voor deze titel, of de bestaande intro's houden?"
        }
        keepLabel={introChosen ? 'Huidige intro houden' : "Bestaande intro's houden"}
        redoLabel={introCost ? `Nieuwe intro's schrijven (${introCost} ${introCost === 1 ? 'credit' : 'credits'})` : "Nieuwe intro's schrijven"}
        onKeep={() => {
          const t = pendingTitle;
          setPendingTitle(null);
          if (t) void commitTitle(t, false);
        }}
        onRedo={() => {
          const t = pendingTitle;
          setPendingTitle(null);
          if (t) void commitTitle(t, true);
        }}
        onClose={() => setPendingTitle(null)}
      />
    </div>
  );
}
